/**
 * The one way the Healtrics console talks to the API (ADR-024).
 *
 * A second client rather than a flag on the first one. The portal's client
 * reads the portal's session and attaches an X-Active-Clinic header that this
 * surface must never send, and threading "which session am I" through every
 * call there would have put the choice at each call site, where it can be made
 * wrongly. Two clients, each reading one session, cannot be confused.
 *
 * Paths and shapes are still checked at compile time against the generated
 * contract, so a call to a path the contract does not describe does not
 * compile, exactly as CLAUDE.md requires.
 */

import createClient from "openapi-fetch";

import type { paths } from "@contract";
import {
  endSession,
  getAccessToken,
  getRefreshToken,
  replaceTokens,
} from "./platformSession";

const BASE_URL = `${import.meta.env.VITE_API_ORIGIN ?? ""}/v1`;

let refreshInFlight: Promise<boolean> | null = null;

async function refresh(): Promise<boolean> {
  const token = getRefreshToken();
  if (!token) return false;

  const response = await fetch(`${BASE_URL}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: token }),
  });

  if (!response.ok) {
    endSession();
    return false;
  }

  replaceTokens(
    (await response.json()) as { access_token: string; refresh_token: string },
  );
  return true;
}

/** One refresh at a time, however many screens ask at once. */
function refreshOnce(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = refresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Bring back a console session that a page reload emptied. */
export function restorePlatformSession(): Promise<boolean> {
  if (getAccessToken() || !getRefreshToken()) {
    return Promise.resolve(Boolean(getAccessToken()));
  }
  return refreshOnce();
}

function authorize(request: Request): Request {
  const token = getAccessToken();
  if (token) {
    request.headers.set("Authorization", `Bearer ${token}`);
  }
  return request;
}

const authenticatedFetch: typeof fetch = async (input, init) => {
  const request = new Request(input, init);
  const retryable = request.clone();

  const response = await fetch(authorize(request));
  if (response.status !== 401) {
    return response;
  }
  if (!(await refreshOnce())) {
    return response;
  }
  return fetch(authorize(retryable));
};

export const platformApi = createClient<paths>({
  baseUrl: BASE_URL,
  fetch: authenticatedFetch,
});
